import { AfterViewInit, Directive, ElementRef, Input } from '@angular/core';
import { safeClone } from '@client/app/shared/utils/object-utils';

class CSV {
    EOL = '\r\n';
    BOM = '\ufeff';

    specialChars = {
        '\\t': '\t',
        '\\b': '\b',
        '\\v': '\v',
        '\\f': '\f',
        '\\r': '\r',
    };

    stringifyField (data, options) {
        if (options.decimalSep === 'locale' && this.isFloat(data)) {
            return data.toLocaleString();
        }

        if (options.decimalSep !== '.' && this.isFloat(data)) {
            return data.toString().replace('.', options.decimalSep);
        }

        if (typeof data === 'string') {
            data = data.replace(/"/g, '""'); // Escape double qoutes

            if (options.quoteStrings || data.indexOf(',') > -1 || data.indexOf('\n') > -1 || data.indexOf('\r') > -1) {
                data = options.txtDelim + data + options.txtDelim;
            }

            return data;
        }

        if (typeof data === 'boolean') {
            return data ? 'TRUE' : 'FALSE';
        }

        return data;
    }

    isFloat (input) {
        return +input === input && (!isFinite(input) || Boolean(input % 1));
    }

    stringify (data, options) {
        return new Promise((resolve, reject) => {
            const that = this;
            let csv = '';
            let csvContent = '';
            const dataPromise = (Array.isArray(data) ? Promise.resolve(data) : data).then(async (responseData) => {
                // responseData = angular.copy(responseData);//moved to row creation
                // Check if there's a provided header array
                if (options.header) {
                    const encodingArray: any[] = [];

                    options.header.forEach((title) => {
                        encodingArray.push(that.stringifyField(title, options));
                    });

                    const headerString = encodingArray.join(options.fieldSep ? options.fieldSep : ',');
                    csvContent += headerString + this.EOL;
                }

                let arrData: any = [];

                if (Array.isArray(responseData)) {
                    arrData = responseData;
                } else if (typeof responseData === 'function') {
                    arrData = await responseData();
                }

                // Check if using keys as labels
                if (options.label && typeof options.label === 'boolean') {
                    const labelArray: any[] = [];

                    const iterator = !!options.columnOrder ? options.columnOrder : arrData[0];
                    iterator.forEach((value, label) => {
                        const val = !!options.columnOrder ? value : label;
                        labelArray.push(that.stringifyField(val, options));
                    });
                    const labelString = labelArray.join(options.fieldSep ? options.fieldSep : ',');
                    csvContent += labelString + this.EOL;
                }

                arrData.forEach((oldRow, index) => {
                    const row = safeClone(arrData[index]);
                    const infoArray: any[] = [];

                    const iterator = !!options.columnOrder ? options.columnOrder : row;
                    iterator.forEach((field, key) => {
                        const val = !!options.columnOrder ? row[field] : field;
                        infoArray.push(that.stringifyField(val, options));
                    });

                    const dataString = infoArray.join(options.fieldSep ? options.fieldSep : ',');
                    csvContent += index < arrData.length ? dataString + this.EOL : dataString;
                });

                // Add BOM if needed
                if (options.addByteOrderMarker) {
                    csv += this.BOM;
                }

                // Append the content and resolve.
                csv += csvContent;
                resolve(csv);
            });

            // if (typeof dataPromise['catch'] === 'function') {
            //     dataPromise['catch'](function (err) {
            //         reject(err);
            //     });
            // }

            return dataPromise;
        });
    }

    isSpecialChar (input) {
        return this.specialChars[input] !== undefined;
    }

    getSpecialChar (input) {
        return this.specialChars[input];
    }
}

@Directive({
    selector: '[ngCsv]',
    standalone: true,
    exportAs: 'ngCsv',
    // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
    inputs: [
        'data',
        'filename',
        'header',
        'columnOrder',
        'txtDelim',
        'decimalSep',
        'quoteStrings',
        'fieldSep',
        'lazyLoad',
        'addByteOrderMarker',
        'charset',
        'label',
    ],
})
export class NgCsvDirective implements AfterViewInit {
    @Input() filename: string = 'download.csv';
    @Input() data: any;
    @Input() header: any;
    @Input() columnOrder: any;
    @Input() txtDelim: string = '"';
    @Input() decimalSep: string = '.';
    @Input() quoteStrings: boolean = false;
    @Input() fieldSep: string = ',';
    @Input() lazyLoad: boolean = false;
    @Input() addByteOrderMarker: boolean = false;
    @Input() charset: string = 'utf-8';
    @Input() label: any;

    csv: any = '';
    CSV = new CSV();
    constructor (
        private elementRef: ElementRef,
    ) {}

    ngAfterViewInit () {
        this.elementRef.nativeElement.addEventListener('click', (e: MouseEvent) => {
            this.buildCSV()
                .then(() => {
                    const charset = this.charset || 'utf-8';
                    const blob = new Blob([this.csv], {
                        type: 'text/csv;charset=' + charset + ';',
                    });

                    const downloadContainer = document.createElement('div');
                    downloadContainer.setAttribute('data-tap-disabled', 'true');
                    const downloadLink = document.createElement('a');
                    downloadContainer.appendChild(downloadLink);
                    downloadLink.setAttribute('href', window.URL.createObjectURL(blob));
                    downloadLink.setAttribute('download', this.getFilename());
                    downloadLink.setAttribute('target', '_blank');

                    document.body.append(downloadContainer);
                    setTimeout(function () {
                        downloadLink.click();
                        downloadLink.remove();
                    });
                });
        });
    }

    getFilename () {
        return this.filename || 'download.csv';
    }

    getBuildCsvOptions () {
        const options: any = {
            txtDelim: this.txtDelim ? this.txtDelim : '"',
            decimalSep: this.decimalSep ? this.decimalSep : '.',
            quoteStrings: this.quoteStrings,
            addByteOrderMarker: this.addByteOrderMarker,
        };
        if (this.elementRef.nativeElement?.getAttribute('csvHeader')) {
            options.header = (this.header);
        }
        if (this.elementRef.nativeElement?.getAttribute('csvColumnOrder')) {
            options.columnOrder = (this.columnOrder);
        }
        if (this.elementRef.nativeElement?.getAttribute('csvLabel')) {
            options.label = (this.label);
        }

        options.fieldSep = this.fieldSep ? this.fieldSep : ',';

        // Replaces any badly formatted special character string with correct special character
        options.fieldSep = this.CSV.isSpecialChar(options.fieldSep) ? this.CSV.getSpecialChar(options.fieldSep) : options.fieldSep;

        return options;
    }

    async buildCSV () {
        let data = null;
        this.elementRef.nativeElement?.classList.add('ng-csv-loading');

        if (typeof this.data === 'function') {
            data = this.data();
        } else {
            data = this.data;
        }

        return this.CSV.stringify(data, this.getBuildCsvOptions()).then((csv) => {
            this.csv = csv;
            this.elementRef.nativeElement?.classList.remove('ng-csv-loading');
            return Promise.resolve(csv);
        });
    }

}
